import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconPlus, IconSearch, IconShare2 } from "@tabler/icons-react";
import ContentHeader from "../app/headers/ContentHeader";
import { NavLang } from "../app/headers/layout";
import ProfilePicture, { type ProfilePictureUser } from "../ProfilePicture";
import { BackButton } from "../ui/BackButton";
import { Button } from "../ui/Button";

type RoomHeaderProps = {
  roomId: string;
  /** Back target of the mobile header (`ContentHeader`, `md:hidden`). */
  backTo: string;
  /**
   * Back target of the wide bar (`md` and up: a tablet browser, a landscape
   * phone), `null` for no back affordance there.
   *
   * Deliberately separate from `backTo`: the two bars are different surfaces
   * and a page can need different targets. The Resources page is the case:
   * the narrow header's back leaves the room, the wide bar's goes back to
   * Discovery.
   */
  desktopBackTo: string | null;
  backLabel: string | null;
  showJoinButton: boolean;
  currentUser: ProfilePictureUser | null;
  onJoinClick: () => void;
  onShareClick: () => void;
};

function RoomActions({
  roomId,
  showJoinButton,
  currentUser,
  onJoinClick,
  onShareClick,
}: {
  roomId: string;
  showJoinButton: boolean;
  currentUser: ProfilePictureUser | null;
  onJoinClick: () => void;
  onShareClick: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();

  // Opens the join sheet — the join itself (and its pending state) lives
  // there, so this button never has a loading state of its own.
  if (showJoinButton) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="h-auto px-3 py-0 text-base gap-1"
        leftIcon={<IconPlus className="size-4.5!" stroke={2} />}
        onClick={onJoinClick}
      >
        {t("Join")}
      </Button>
    );
  }

  return (
    <>
      <IconSearch
        className="cursor-pointer text-primary-black size-6 stroke-[1.75px]"
        onClick={() => {
          history.push(`/room/search/${roomId}`);
        }}
      />
      <NavLang />
      <IconShare2
        className="cursor-pointer text-primary-black size-6 stroke-[1.75px]"
        onClick={onShareClick}
      />
      {currentUser !== null ? (
        <Link to="/profile">
          <ProfilePicture user={currentUser} height={32} badge={null} />
        </Link>
      ) : null}
    </>
  );
}

export function RoomHeader({
  roomId,
  backTo,
  desktopBackTo,
  backLabel,
  showJoinButton,
  currentUser,
  onJoinClick,
  onShareClick,
}: RoomHeaderProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <>
      <ContentHeader
        transparent
        backLabel={backLabel === null ? undefined : backLabel}
        backTo={backTo}
        actions={
          <RoomActions
            roomId={roomId}
            showJoinButton={showJoinButton}
            currentUser={currentUser}
            onJoinClick={onJoinClick}
            onShareClick={onShareClick}
          />
        }
      />
      <div className="hidden md:flex items-center justify-end gap-4 px-5 py-3">
        {desktopBackTo !== null ? (
          // `mr-auto` keeps the actions right-aligned as they are without a back.
          <BackButton to={desktopBackTo} className="mr-auto">
            {backLabel === null ? t("Back") : backLabel}
          </BackButton>
        ) : null}
        <RoomActions
          roomId={roomId}
          showJoinButton={showJoinButton}
          currentUser={currentUser}
          onJoinClick={onJoinClick}
          onShareClick={onShareClick}
        />
      </div>
    </>
  );
}
